class StoreIndexComponent extends HTMLElement {
  constructor() {
    super();
    this.stores = [];
    this.filteredStores = [];
    this.groupBy = 'state';
    this.searchTerm = '';
    this.typeFilters = {
      off: true,
      on: true
    };
    this.debounceTimer = null;
  }

  connectedCallback() {
    this.apiEndpoint = this.dataset.apiEndpoint;
    this.groupBy = this.dataset.groupBy || 'state';
    this.sortOrder = this.dataset.sortOrder || 'asc';

    this.initializeRefs();
    this.attachEventListeners();
    this.loadStores();
  }

  initializeRefs() {
    this.refs = {};
    this.querySelectorAll('[data-ref]').forEach(element => {
      this.refs[element.dataset.ref] = element;
    });
  }

  attachEventListeners() {
    // Group by buttons
    this.querySelectorAll('[data-group]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.querySelectorAll('[data-group]').forEach(b => b.classList.remove('active'));
        e.target.classList.add('active');
        this.groupBy = e.target.dataset.group;
        this.renderStores();
      });
    });

    // Type filters
    if (this.refs.filterOffPremise) {
      this.refs.filterOffPremise.addEventListener('change', (e) => {
        this.typeFilters.off = e.target.checked;
        this.filterStores();
      });
    }

    if (this.refs.filterOnPremise) {
      this.refs.filterOnPremise.addEventListener('change', (e) => {
        this.typeFilters.on = e.target.checked;
        this.filterStores();
      });
    }

    // Search input
    if (this.refs.searchInput) {
      this.refs.searchInput.addEventListener('input', (e) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.searchTerm = e.target.value.toLowerCase();
          this.filterStores();
        }, 300);
      });
    }

    // Smooth scroll for navigation
    this.addEventListener('click', (e) => {
      if (e.target.matches('.index-nav-link')) {
        e.preventDefault();
        const targetId = e.target.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });
  }

  async loadStores() {
    if (!this.apiEndpoint) {
      console.error('No API endpoint configured');
      this.showError('Store directory configuration error');
      return;
    }

    try {
      this.showLoading(true);

      // Fetch all stores (no location-based filtering for index)
      const response = await fetch(`${this.apiEndpoint}?all=true`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.stores = data.stores || [];

      // Initial filter and render
      this.filterStores();

    } catch (error) {
      console.error('Error loading stores:', error);
      this.showError('Unable to load store directory. Please try again later.');
    } finally {
      this.showLoading(false);
    }
  }

  filterStores() {
    this.filteredStores = this.stores.filter(store => {
      // Type filter
      const storeType = store.type?.toLowerCase() || 'off';
      if (storeType === 'off' && !this.typeFilters.off) return false;
      if (storeType === 'on' && !this.typeFilters.on) return false;

      // Search filter
      if (this.searchTerm) {
        const searchableText = `
          ${store.name || ''}
          ${store.address || ''}
          ${store.city || ''}
          ${store.state || ''}
          ${store.zip || ''}
        `.toLowerCase();

        if (!searchableText.includes(this.searchTerm)) {
          return false;
        }
      }

      return true;
    });

    this.renderStores();
  }

  renderStores() {
    if (this.filteredStores.length === 0) {
      this.showEmpty(true);
      return;
    }

    this.showEmpty(false);

    const grouped = this.groupStores(this.filteredStores);
    this.renderNavigation(grouped);
    this.renderDirectory(grouped);
  }

  groupStores(stores) {
    const groups = {};

    stores.forEach(store => {
      let key;

      switch (this.groupBy) {
        case 'state':
          key = store.state || 'Unknown';
          break;
        case 'city':
          // Handle duplicate city names by including state
          const city = store.city || 'Unknown';
          const state = store.state || '';
          key = state ? `${city}, ${state}` : city;
          break;
        case 'alpha':
          key = (store.name || 'Unknown').charAt(0).toUpperCase();
          break;
        default:
          key = 'All';
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(store);
    });

    // Sort groups
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (this.sortOrder === 'desc') {
        return b.localeCompare(a);
      }
      return a.localeCompare(b);
    });

    // Sort stores within each group
    sortedKeys.forEach(key => {
      groups[key].sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.localeCompare(nameB);
      });
    });

    return sortedKeys.reduce((sorted, key) => {
      sorted[key] = groups[key];
      return sorted;
    }, {});
  }

  renderNavigation(grouped) {
    if (!this.refs.indexNav) return;

    const keys = Object.keys(grouped);
    if (keys.length <= 1) {
      this.refs.indexNav.style.display = 'none';
      return;
    }

    this.refs.indexNav.style.display = 'flex';

    const navHTML = keys.map(key => {
      const anchorId = this.handleize(key);
      const count = grouped[key].length;
      return `
        <a href="#${anchorId}" class="index-nav-link">
          ${this.escapeHtml(key)}
          <span class="nav-count">(${count})</span>
        </a>
      `;
    }).join('');

    this.refs.indexNav.innerHTML = navHTML;
  }

  renderDirectory(grouped) {
    if (!this.refs.storeGroups) return;

    const groupsHTML = Object.entries(grouped).map(([key, stores]) => {
      const anchorId = this.handleize(key);
      const storesHTML = stores.map(store => this.renderStore(store)).join('');

      return `
        <div class="store-group" id="${anchorId}">
          <h3 class="store-group-header">
            <span class="group-title">${this.escapeHtml(key)}</span>
            <span class="group-count">${stores.length} ${stores.length === 1 ? 'location' : 'locations'}</span>
          </h3>
          <div class="store-group-list">
            ${storesHTML}
          </div>
        </div>
      `;
    }).join('');

    this.refs.storeGroups.innerHTML = groupsHTML;
  }

  renderStore(store) {
    const typeClass = store.type?.toLowerCase() === 'on' ? 'type-on' : 'type-off';
    const typeBadge = store.type?.toLowerCase() === 'on'
      ? '<span class="store-badge badge-bar">Bar/Restaurant</span>'
      : '<span class="store-badge badge-store">Store</span>';

    const phone = store.phone
      ? `<a href="tel:${store.phone}" class="store-phone">${store.phone}</a>`
      : '';

    const directions = store.latitude && store.longitude
      ? `<a href="https://maps.google.com/?q=${store.latitude},${store.longitude}"
           target="_blank"
           rel="noopener noreferrer"
           class="store-directions">Get Directions</a>`
      : '';

    return `
      <div class="store-item ${typeClass}">
        <div class="store-item-header">
          <h4 class="store-name">${store.name || 'Unnamed Store'}</h4>
          ${typeBadge}
        </div>
        <div class="store-item-details">
          <address class="store-address">
            ${store.address || ''}<br>
            ${store.city || ''}, ${store.state || ''} ${store.zip || ''}
          </address>
          <div class="store-actions">
            ${phone}
            ${directions}
          </div>
        </div>
      </div>
    `;
  }

  handleize(str) {
    // Convert string to URL-safe handle (like Shopify's handleize)
    return str
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  showLoading(show) {
    if (this.refs.loadingState) {
      this.refs.loadingState.hidden = !show;
    }
    if (this.refs.storeGroups) {
      this.refs.storeGroups.hidden = show;
    }
  }

  showEmpty(show) {
    if (this.refs.emptyState) {
      this.refs.emptyState.hidden = !show;
    }
    if (this.refs.storeGroups) {
      this.refs.storeGroups.hidden = show;
    }
    if (this.refs.indexNav && show) {
      this.refs.indexNav.style.display = 'none';
    }
  }

  showError(message) {
    if (this.refs.storeGroups) {
      this.refs.storeGroups.innerHTML = `
        <div class="error-message">
          <p>${message}</p>
        </div>
      `;
    }
    this.showLoading(false);
  }
}

customElements.define('store-index-component', StoreIndexComponent);